import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "space-y-3" }
const _hoisted_2 = { class: "rounded-lg border border-gray-200 bg-white p-3" }
const _hoisted_3 = { class: "mb-1 text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_4 = {
  key: 1,
  class: "flex h-[140px] items-center justify-center text-center text-xs italic text-gray-400"
}
const _hoisted_5 = { class: "flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5" }
const _hoisted_6 = { class: "shrink-0 text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_7 = { class: "truncate text-xs italic text-gray-400" }
const _hoisted_8 = { class: "rounded-lg border border-gray-200 bg-white p-3" }
const _hoisted_9 = { class: "mb-1 text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400" }

import { computed } from 'vue';
import merge from 'lodash-es/merge';
import GenericChart from '@/components/charts/GenericChart.vue';
import useDefaultChartOptions from '@/components/charts/composables/useDefaultChartOptions';
import { useSyncedChartExtremes } from '@/composables/useSyncedChartExtremes';
import { i18n } from '@/plugins/internationalization/i18n';
import { ChartType, ProductionGroup } from '@/types';
import type { PigTallyDay } from '@/types/SectionLiveKpi';
import type { BackfillDraft } from './useProductionGroupBackfill';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Rough pixel budget for a plot-band label before it starts crowding its neighbour. The dialog this
// renders in is a fixed max-width (900px, see BackfillWizardModal.vue), so the chart's plot area is a
// fairly stable width - but an existing group's name is free text (farmer-typed), so on a narrow band
// (a short-lived section, or several groups bunched up near "today") the full name doesn't fit and
// visibly overlaps the next band's label (seen live with e.g. "28/1-28/5" running into "IQB
// testhold"). Truncating by an estimated character budget keeps every band's label legible without
// needing the chart's actual rendered pixel width at compute time.
const PLOT_WIDTH_PX = 620;
const AVG_LABEL_CHAR_PX = 6.5;
// Two neighbouring bands' labels collide well before either one's text alone overflows its own
// band - Highcharts centers each plotBand label independently with no awareness of its neighbours -
// so the usable budget is a fraction of the band's own width, leaving clearance on both sides.
const BAND_LABEL_SAFETY_FACTOR = 0.5;

type BackfillChartId = 'pigTally' | 'groups';

export default /*@__PURE__*/_defineComponent({
  __name: 'BackfillTimeline',
  props: {
    tally: {},
    existingGroups: {},
    suggestions: {},
    from: {},
    today: {}
  },
  setup(__props: any) {

const props = __props;

// Marks where measured history stops on every lane, so an open-ended suggestion running to the
// right edge reads as "still ongoing" rather than as data continuing past today.
const todayPlotLine = computed(() => ({
    value: Date.parse(props.today),
    color: '#111827',
    dashStyle: 'Dash',
    width: 1,
    zIndex: 5,
    label: {
        text: i18n.global.t('views.monitoring.groupOverview.modals.backfill.timeline.today') as string,
        rotation: 0,
        y: 12,
        style: { color: '#111827', fontSize: '10px', fontWeight: '600' },
    },
}));

// Every lane is its own chart, so they only line up if each one is pinned to the same range. This
// also overrides the min/max useDefaultChartOptions takes from the global period picker, which has
// nothing to do with the range the wizard is detecting over. tickInterval is left to Highcharts' own
// placement (not the app-wide weekly default) - the wizard's range routinely spans years, and a
// forced weekly tick crams unreadable labels across it.
//
// labels.format overrides the app-wide default of "{value:%W.%y}" (an ISO week number, e.g.
// "18.24") - meaningful on the app's other charts, which are all pinned to the period picker's
// (at most a few months) range, but not to a farmer skimming a multi-year wizard timeline. Month/year
// ("Apr '24") matches what the pig-tally lane's own navigator strip already shows underneath it.
const sharedXAxis = computed(() => ({
    min: Date.parse(props.from),
    max: Date.parse(props.today),
    tickInterval: null,
    labels: { format: "{value:%b '%y}" },
    plotLines: [todayPlotLine.value],
}));

function truncateForBandWidth(text: string, bandDays: number, totalDays: number): string {
    if (!text || totalDays <= 0) return text;
    const bandPx = (bandDays / totalDays) * PLOT_WIDTH_PX * BAND_LABEL_SAFETY_FACTOR;
    const maxChars = Math.floor(bandPx / AVG_LABEL_CHAR_PX);
    if (text.length <= maxChars) return text;
    if (maxChars <= 1) return '';
    return `${text.slice(0, maxChars - 1)}…`;
}

// Existing groups: inert gray bands, never interactive here (editing stays in ManageProductionGroupModal.vue).
// Suggestions: accent bands, numbered, dimmed/outlined when discarded. Drawn on the pig-tally lane
// itself (not only the thin groups strip below it) so a span is visible against the data it covers.
const groupsPlotBands = computed(() => {
    const totalDays = (Date.parse(props.today) - Date.parse(props.from)) / MS_PER_DAY;

    const existing = props.existingGroups.map((group) => {
        const from = Date.parse(group.start);
        const to = Date.parse(group.end || props.today);
        return {
            from,
            to,
            color: 'rgba(156, 163, 175, 0.25)',
            label: { text: truncateForBandWidth(group.name, (to - from) / MS_PER_DAY, totalDays), style: { color: '#6B7280', fontSize: '10px', fontWeight: '500' }, y: 14 },
        };
    });

    const suggested = props.suggestions.map((suggestion, index) => ({
        from: Date.parse(suggestion.start),
        to: Date.parse(suggestion.end || props.today),
        color: suggestion.accepted ? 'rgba(37, 99, 235, 0.28)' : 'rgba(156, 163, 175, 0.1)',
        borderColor: suggestion.accepted ? undefined : '#9CA3AF',
        borderWidth: suggestion.accepted ? 0 : 1,
        label: { text: `#${index + 1}`, style: { color: suggestion.accepted ? '#1D4ED8' : '#9CA3AF', fontSize: '10px', fontWeight: '600' }, y: 14 },
    }));

    return [...existing, ...suggested];
});

// The groups lane's own bands (unlike the pig-tally lane's, which sit over a data curve) run edge
// to edge with no data underneath, so a label drawn straight onto the fill reads as sitting "on"
// the band with no room around it. A small padded backdrop behind the text is what puts space
// between the two without needing to move the label outside the band (Highcharts always draws a
// plotBand across the full plot height, so there is nowhere outside it to move to).
const groupsLanePlotBands = computed(() =>
    groupsPlotBands.value.map((band) => ({
        ...band,
        label: { ...band.label, backgroundColor: 'rgba(255, 255, 255, 0.85)', borderRadius: 3, padding: 3, y: 16 },
    }))
);

// Stock-type lanes (not plain charts): a Stock chart carries a navigator, which is what makes the
// pig-tally lane below draggable/zoomable over a range that can run for years.
const laneDefaults = useDefaultChartOptions({}, { type: ChartType.Stock }, true);

// One range for the pig-tally lane and the groups strip beneath it: the navigator lives on the
// pig-tally chart (it has real data to draw a meaningful overview sparkline from), and a drag there
// is mirrored onto the groups strip so the two never show two different windows.
const BACKFILL_CHART_IDS: BackfillChartId[] = ['pigTally', 'groups'];
const { register: registerChart, onSetExtremes: syncChartExtremes } = useSyncedChartExtremes(BACKFILL_CHART_IDS);

function onChartRendered(id: BackfillChartId, instance: any): void {
    registerChart(id, instance);
}

const pigTallyChartOptions = computed(() =>
    merge({}, laneDefaults.value, {
        chart: { height: 140, spacingLeft: 0, spacingRight: 0, backgroundColor: 'transparent', zoomType: null },
        title: { text: '' },
        exporting: { enabled: false },
        legend: { enabled: false },
        rangeSelector: { enabled: false },
        scrollbar: { enabled: false },
        navigator: { enabled: true, height: 26, margin: 8 },
        xAxis: {
            ...sharedXAxis.value,
            plotBands: groupsPlotBands.value,
            events: { setExtremes: (event: any) => syncChartExtremes(event, 'pigTally') },
        },
        yAxis: { softMin: 0, allowDecimals: false },
        tooltip: { shared: true, split: false },
        series: [
            {
                type: 'line',
                name: i18n.global.t('views.monitoring.groupOverview.modals.backfill.timeline.pigTallyLane') as string,
                data: props.tally.map((point) => [Date.parse(point.date), point.pigs]),
                marker: { enabled: false },
                dataGrouping: { enabled: false },
            },
        ],
    })
);

const groupsChartOptions = computed(() =>
    merge({}, laneDefaults.value, {
        chart: { height: 70, spacingLeft: 0, spacingRight: 0, backgroundColor: 'transparent', zoomType: null },
        title: { text: '' },
        exporting: { enabled: false },
        legend: { enabled: false },
        rangeSelector: { enabled: false },
        scrollbar: { enabled: false },
        // No navigator of its own: this strip follows the pig-tally lane's, see above.
        navigator: { enabled: false },
        xAxis: {
            ...sharedXAxis.value,
            // This lane is a thin 70px strip directly under the pig-tally lane's own (synced) date
            // axis - its own copy of the same labels is redundant, and the month/year format (rotated
            // -90deg, so a label's text length becomes vertical height) does not fit this lane's short
            // chart height without clipping.
            labels: { enabled: false },
            plotBands: groupsLanePlotBands.value,
            events: { setExtremes: (event: any) => syncChartExtremes(event, 'groups') },
        },
        yAxis: { visible: false },
        tooltip: { enabled: false },
        series: [{ type: 'line', data: [], enableMouseTracking: false, showInLegend: false }],
    })
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.timeline.pigTallyLane')), 1),
      (_ctx.tally.length)
        ? (_openBlock(), _createBlock(GenericChart, {
            key: 0,
            options: pigTallyChartOptions.value,
            expandable: false,
            onReady: _cache[0] || (_cache[0] = ($event: any) => (onChartRendered('pigTally', $event))),
            onUpdate: _cache[1] || (_cache[1] = ($event: any) => (onChartRendered('pigTally', $event)))
          }, null, 8, ["options"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.timeline.pigTallyEmpty')), 1))
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.timeline.waterLane')) + " / " + _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.timeline.weightLane')), 1),
      _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.timeline.telemetryLaneUnavailable')), 1)
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createElementVNode("p", _hoisted_9, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.timeline.groupsLane')), 1),
      _createVNode(GenericChart, {
        options: groupsChartOptions.value,
        expandable: false,
        onReady: _cache[2] || (_cache[2] = ($event: any) => (onChartRendered('groups', $event))),
        onUpdate: _cache[3] || (_cache[3] = ($event: any) => (onChartRendered('groups', $event)))
      }, null, 8, ["options"])
    ])
  ]))
}
}

})